import React, { useLayoutEffect, useRef, useState } from "react";

/**
 * La barra de acciones de la pantalla "Subir productos" de Mi marca
 * (ADR-2026-10-13-subir-productos-es-una-pantalla), con el fondo del lienzo (`#F9F9F9`)
 * para que lo que pasa por debajo no se lea a través. La usan las dos formas de subir
 * —un producto (`SubirProductos`) y varios (`AgregarVariosModal` con `enPagina`)— para
 * que el CTA quede en el mismo lugar.
 *
 * Cómo se queda abajo depende del shell, no del gusto:
 *   • Teléfono (< `md`): `fixed` contra el borde de la pantalla. El shell mobile envuelve
 *     el contenido en contenedores con `overflow-x-hidden`, que los vuelve contenedores de
 *     scroll que nunca scrollean: un `sticky` adentro se queda donde cae, al final.
 *     Un espaciador con el alto medido de la barra evita que tape lo último.
 *   • Tablet (`md`–`lg`): en el flujo, al final. Mismo shell, y con su sidebar al costado
 *     una barra `fixed` de borde a borde le pasaría por debajo.
 *   • Escritorio (`lg`): `sticky`, pegada abajo mientras se scrollea.
 *
 * `arriba` es lo que va pegado encima de los botones (avisos, el producto levantado).
 */
export const PieFijoDeAlta: React.FC<{
  arriba?: React.ReactNode;
  /**
   * Sólo hasta `lg`: en escritorio la pantalla pone sus botones arriba, junto al título
   * (un solo producto — ADR-2026-10-13-un-solo-producto-acciones-arriba).
   */
  hastaLg?: boolean;
  children: React.ReactNode;
}> = ({ arriba, hastaLg = false, children }) => {
  const barraRef = useRef<HTMLDivElement>(null);
  const [alto, setAlto] = useState(0);
  useLayoutEffect(() => {
    const el = barraRef.current;
    if (!el) return;
    const medir = () => setAlto(el.offsetHeight);
    medir();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <>
      <div aria-hidden className="md:hidden" style={{ height: alto + 24 }} />
      <div
        ref={barraRef}
        className={`${hastaLg ? "lg:hidden " : ""}fixed inset-x-0 bottom-0 z-40 border-t border-black/[0.06] bg-[#F9F9F9]/95 px-4 pt-3 backdrop-blur-md md:static md:mt-8 md:px-0 lg:sticky lg:bottom-0 lg:z-10`}
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
      >
        {arriba}
        <div className="flex items-center justify-end gap-2">{children}</div>
      </div>
    </>
  );
};

/** El CTA de la barra: a todo el ancho en el teléfono, a medida desde `sm`. */
export const BotonDeAlta: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled = false, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-full px-5 text-[15px] font-semibold transition-colors sm:h-10 sm:flex-none sm:text-[14px] ${
      disabled
        ? "cursor-not-allowed bg-black/[0.05] text-black/30"
        : "bg-brand-primary text-white shadow-[0_6px_16px_-6px_rgb(var(--brand-primary-rgb)_/_0.55)] hover:bg-brand-primary-hover"
    }`}
  >
    {children}
  </button>
);

/** "Cancelar", al lado del CTA. Sólo desde `sm`: en el teléfono se sale con el volver. */
export const BotonCancelarDeAlta: React.FC<{
  onClick: () => void;
  disabled?: boolean;
}> = ({ onClick, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="hidden h-10 items-center rounded-full px-4 text-[14px] font-medium text-black/55 transition-colors hover:bg-black/[0.04] hover:text-[#16181D] disabled:cursor-not-allowed disabled:opacity-50 sm:inline-flex"
  >
    Cancelar
  </button>
);
